import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

/**
 * Real India-states map (TopoJSON from public/india-states.topo.json).
 *
 * We highlight served regions in a lighter fill and overlay marker dots for
 * dispatch depots with a pulsing halo. All decorative motion.
 */

// State names (must match `st_nm` in the topojson properties)
// Pan-India coverage. Southern + Western states are the primary dispatch region
// (out of the six warehouses: Coimbatore, Chennai, Cochin, Bangalore, Dahej, Mumbai).
// Everywhere else is served via scheduled tanker routes.
const SERVED_STATES = [
  // Primary — direct from our warehouses
  { name: "Tamil Nadu", primary: true, note: "Direct from Coimbatore & Chennai warehouses" },
  { name: "Kerala", primary: true, note: "Direct from Cochin warehouse" },
  { name: "Karnataka", primary: true, note: "Direct from Bangalore warehouse" },
  { name: "Andhra Pradesh", primary: true, note: "From Chennai & Bangalore warehouses" },
  { name: "Telangana", primary: true, note: "From Chennai & Bangalore warehouses" },
  { name: "Maharashtra", primary: true, note: "Direct from Mumbai warehouse" },
  { name: "Gujarat", primary: true, note: "Direct from Dahej warehouse" },
  // Served — pan-India via scheduled tanker routes
  { name: "Madhya Pradesh", primary: false, note: "Scheduled tanker routes" },
  { name: "Chhattisgarh", primary: false, note: "Scheduled tanker routes" },
  { name: "Odisha", primary: false, note: "Scheduled tanker routes" },
  { name: "West Bengal", primary: false, note: "Scheduled tanker routes" },
  { name: "Jharkhand", primary: false, note: "Scheduled tanker routes" },
  { name: "Bihar", primary: false, note: "Scheduled tanker routes" },
  { name: "Uttar Pradesh", primary: false, note: "Scheduled tanker routes" },
  { name: "Uttarakhand", primary: false, note: "Scheduled tanker routes" },
  { name: "Rajasthan", primary: false, note: "Scheduled tanker routes" },
  { name: "Haryana", primary: false, note: "Scheduled tanker routes" },
  { name: "Punjab", primary: false, note: "Scheduled tanker routes" },
  { name: "Delhi", primary: false, note: "Scheduled tanker routes" },
  { name: "Himachal Pradesh", primary: false, note: "By arrangement" },
  { name: "Jammu and Kashmir", primary: false, note: "By arrangement" },
  { name: "Assam", primary: false, note: "By arrangement" },
  { name: "Goa", primary: false, note: "By arrangement" },
];
const SERVED_INDEX = Object.fromEntries(SERVED_STATES.map((s) => [s.name, s]));

// Depot geo coordinates (lng, lat) — used with react-simple-maps' Marker projection.
const DEPOTS = [
  { key: "coimbatore", label: "Coimbatore", city: "Head office", coords: [76.9558, 11.0168], primary: true },
  { key: "chennai", label: "Chennai", city: "East-coast dispatch", coords: [80.2707, 13.0827], primary: true },
  { key: "cochin", label: "Cochin", city: "Kerala dispatch", coords: [76.2673, 9.9312] },
  { key: "bangalore", label: "Bangalore", city: "Karnataka & Andhra dispatch", coords: [77.5946, 12.9716] },
  { key: "mumbai", label: "Mumbai", city: "Maharashtra & west-coast dispatch", coords: [72.8777, 19.0760], primary: true },
  { key: "dahej", label: "Dahej", city: "Gujarat industrial belt", coords: [72.5750, 21.7020], primary: true },
];

// Region markers to which we draw route lines (rough state centroids)
const REGION_ANCHORS = {
  "Tamil Nadu": [78.5, 11.1],
  Kerala: [76.3, 10.5],
  Karnataka: [76.0, 14.5],
  "Andhra Pradesh": [79.5, 15.9],
  Telangana: [79.0, 18.1],
  Gujarat: [71.8, 22.5],
  Maharashtra: [75.8, 19.5],
  Rajasthan: [74.2, 26.5],
  "Madhya Pradesh": [78.5, 23.5],
  Odisha: [85.0, 20.9],
  "West Bengal": [87.9, 23.5],
  "Uttar Pradesh": [80.9, 26.8],
};

const ROUTES = [
  ["coimbatore", "Tamil Nadu"],
  ["coimbatore", "Kerala"],
  ["chennai", "Andhra Pradesh"],
  ["chennai", "Telangana"],
  ["cochin", "Kerala"],
  ["bangalore", "Karnataka"],
  ["mumbai", "Maharashtra"],
  ["dahej", "Gujarat"],
  ["dahej", "Madhya Pradesh"],
  ["chennai", "Odisha"],
  ["chennai", "West Bengal"],
  ["mumbai", "Madhya Pradesh"],
  ["dahej", "Rajasthan"],
  ["bangalore", "Uttar Pradesh"],
];

// India centroid + scale tuned for the current viewBox
const PROJECTION_CONFIG = { scale: 900, center: [82, 22] };

// react-simple-maps returns a projection via context; we replicate its projection
// manually for markers using d3-geo-style math. Simpler: use Marker component.
import { Marker, Line } from "react-simple-maps";

export default function AnimatedIndiaMap() {
  const [hover, setHover] = useState(null); // state name currently hovered
  const [error, setError] = useState(null);

  // Sanity check: file exists
  useEffect(() => {
    fetch(process.env.PUBLIC_URL + "/india-states.topo.json", { method: "HEAD" })
      .then((r) => {
        if (!r.ok) setError("Map data failed to load.");
      })
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div className="india-map" data-testid="india-map">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={PROJECTION_CONFIG}
        width={520}
        height={560}
        style={{ width: "100%", height: "auto", display: "block" }}
      >
        <defs>
          <radialGradient id="pulse-primary">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pulse-secondary">
            <stop offset="0%" stopColor="#F1B4B5" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#F1B4B5" stopOpacity="0" />
          </radialGradient>
        </defs>

        <Geographies geography={process.env.PUBLIC_URL + "/india-states.topo.json"}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const name = geo.properties.st_nm;
              const served = SERVED_INDEX[name];
              const isHovered = hover === name;
              const fill = served
                ? served.primary
                  ? "#D63031"
                  : "#8E2224"
                : "#243441";
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => setHover(name)}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`state-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  style={{
                    default: {
                      fill: isHovered ? "#E54446" : fill,
                      stroke: "rgba(255, 255, 255, 0.22)",
                      strokeWidth: 0.35,
                      outline: "none",
                      transition: "fill 0.2s ease",
                    },
                    hover: {
                      fill: "#E54446",
                      outline: "none",
                      cursor: served ? "pointer" : "default",
                    },
                    pressed: { fill: "#E54446", outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>

        {/* Tanker routes (animated dashed lines) */}
        {ROUTES.map(([dKey, regionName], i) => {
          const depot = DEPOTS.find((d) => d.key === dKey);
          const region = REGION_ANCHORS[regionName];
          if (!depot || !region) return null;
          return (
            <Line
              key={`route-${i}`}
              from={depot.coords}
              to={region}
              stroke="#F1B4B5"
              strokeWidth={0.8}
              strokeDasharray="3 2"
              strokeLinecap="round"
              data-testid={`route-${dKey}-${regionName.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            />
          );
        })}

        {/* Depot markers */}
        {DEPOTS.map((d, i) => (
          <Marker key={d.key} coordinates={d.coords} data-testid={`depot-dot-${d.key}`}>
            <motion.circle
              r={11}
              fill={d.primary ? "url(#pulse-primary)" : "url(#pulse-secondary)"}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.4, 0.9, 1.4], opacity: [0, 0.6, 0.3, 0.6] }}
              transition={{ duration: 2.4, delay: 0.4 + i * 0.2, repeat: Infinity, ease: "easeInOut" }}
              style={{ transformOrigin: "center", transformBox: "fill-box" }}
            />
            <motion.circle
              r={d.primary ? 4 : 3.2}
              fill={d.primary ? "#ffffff" : "#ffffff"}
              stroke="#101820"
              strokeWidth={0.8}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 + i * 0.15, duration: 0.4, ease: "backOut" }}
              style={{ transformOrigin: "center", transformBox: "fill-box" }}
            />
            <text
              textAnchor="middle"
              y={-8}
              fontFamily="Sora, sans-serif"
              fontSize={7}
              fontWeight={700}
              fill="#ffffff"
              style={{ paintOrder: "stroke", stroke: "#101820", strokeWidth: 2, strokeLinejoin: "round" }}
            >
              {d.label}
            </text>
          </Marker>
        ))}
      </ComposableMap>

      {/* Legend + hover readout */}
      <div className="india-map-legend" data-testid="india-map-legend">
        <div className="india-map-legend-row">
          <span className="legend-swatch primary" />
          Primary dispatch zone (six warehouses across South &amp; West India)
        </div>
        <div className="india-map-legend-row">
          <span className="legend-swatch served" />
          Regular tanker routes
        </div>
        {hover && (
          <div className="india-map-hover" data-testid="india-map-hover">
            <b>{hover}</b>
            {SERVED_INDEX[hover] ? ` — ${SERVED_INDEX[hover].note}` : " — by-arrangement dispatch, confirm lead time with sales"}
          </div>
        )}
        {error && <div className="note" style={{ color: "#ff8368" }}>{error}</div>}
      </div>
    </div>
  );
}
